import { useCallback, useEffect, useState } from 'react';

/**
 * The collapse state of the staff sidebar: a 216px panel with labels, or a
 * 48px icon rail.
 *
 * The state persists in the `septima-sidebar` cookie, the same way that the
 * theme persists in `septima-theme` (ThemeContext). The application uses no
 * localStorage. The first render reads the cookie, so the sidebar does not
 * flash from one width to the other on a reload.
 *
 * When the cookie is absent, the width of the window sets the default: below
 * 1280px the sidebar starts as a rail. Until the reader makes a choice, the
 * default follows the window when it crosses 1280px. Below `lg` (1024px) the
 * mobile drawer replaces the sidebar, and the drawer always shows labels.
 */

export const SIDEBAR_COOKIE = 'septima-sidebar';

/** The window width, in px, at and above which the sidebar opens expanded by default. */
export const SIDEBAR_EXPANDED_MIN_WIDTH = 1280;

export type SidebarPreference = 'collapsed' | 'expanded';

/** Read the saved preference from a `document.cookie` string. Return null when there is none. */
export function readSidebarPreference(cookie: string): SidebarPreference | null {
    const match = cookie.match(new RegExp(`(^|;\\s*)${SIDEBAR_COOKIE}=([^;]+)`));
    const value = match ? match[2] : null;
    return value === 'collapsed' || value === 'expanded' ? value : null;
}

/** The first state: the saved preference wins, else the width of the window decides. */
export function initialCollapsed(
    preference: SidebarPreference | null,
    viewportWidth: number,
): boolean {
    if (preference) return preference === 'collapsed';
    return viewportWidth < SIDEBAR_EXPANDED_MIN_WIDTH;
}

/** The part of an event target that `isEditableTarget` reads. A DOM Element satisfies it. */
export interface EditableProbe {
    tagName?: string;
    isContentEditable?: boolean;
    getAttribute?: (name: string) => string | null;
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * True when the focus is in a control that takes text or a choice. The toggle
 * shortcut must not fire there: `[` is a character that the reader types.
 */
export function isEditableTarget(target: EditableProbe | null | undefined): boolean {
    if (!target) return false;
    if (target.tagName && EDITABLE_TAGS.has(target.tagName.toUpperCase())) return true;
    if (target.isContentEditable) return true;
    const role = target.getAttribute?.('role');
    return role === 'textbox' || role === 'combobox' || role === 'searchbox';
}

/** The part of a KeyboardEvent that `isSidebarToggleKey` reads. */
export interface ToggleKeyProbe {
    key: string;
    code?: string;
    metaKey: boolean;
    ctrlKey: boolean;
    altKey: boolean;
    shiftKey: boolean;
    target: EventTarget | EditableProbe | null;
}

/**
 * True for the two toggle shortcuts: Cmd+\ or Ctrl+\, and a bare `[`.
 * Both are false when the focus is in an editable control.
 */
export function isSidebarToggleKey(event: ToggleKeyProbe): boolean {
    if (isEditableTarget(event.target as EditableProbe | null)) return false;
    if (event.altKey || event.shiftKey) return false;
    const modifier = event.metaKey || event.ctrlKey;
    if (modifier) return event.key === '\\' || event.code === 'Backslash';
    return event.key === '[';
}

/** The label of the shortcut for a tooltip: "⌘\" on a Mac, "Ctrl+\" in other cases. */
export function sidebarShortcutLabel(platform: string): string {
    return /mac|iphone|ipad/i.test(platform) ? '⌘\\' : 'Ctrl+\\';
}

function writeSidebarCookie(value: SidebarPreference) {
    if (typeof document === 'undefined') return;
    const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
    document.cookie = `${SIDEBAR_COOKIE}=${value}; expires=${expires}; path=/; SameSite=Lax`;
}

function readInitialState(): boolean {
    if (typeof document === 'undefined' || typeof window === 'undefined') return false;
    return initialCollapsed(readSidebarPreference(document.cookie), window.innerWidth);
}

export interface SidebarCollapse {
    collapsed: boolean;
    toggle: () => void;
    /** The shortcut label for the toggle tooltip, for the platform of the reader. */
    shortcutLabel: string;
}

export function useSidebarCollapse(): SidebarCollapse {
    const [collapsed, setCollapsed] = useState<boolean>(readInitialState);

    const toggle = useCallback(() => {
        setCollapsed((prev) => {
            const next = !prev;
            writeSidebarCookie(next ? 'collapsed' : 'expanded');
            return next;
        });
    }, []);

    // With no saved choice, follow the window across the 1280px line.
    useEffect(() => {
        const query = window.matchMedia(`(min-width: ${SIDEBAR_EXPANDED_MIN_WIDTH}px)`);
        const onChange = (e: MediaQueryListEvent) => {
            if (readSidebarPreference(document.cookie)) return;
            setCollapsed(!e.matches);
        };
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.repeat) return;
            if (!isSidebarToggleKey(e)) return;
            e.preventDefault();
            toggle();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [toggle]);

    const platform = typeof navigator === 'undefined' ? '' : navigator.platform;
    return { collapsed, toggle, shortcutLabel: sidebarShortcutLabel(platform) };
}
